import React, { useEffect, useState, useMemo } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Button } from "@chakra-ui/react";
import axios from "axios";
import { ImSpinner8 } from "react-icons/im";
import FeeTemplateSectionReadOnly from "./FeeTemplateSectionReadOnly";
import { getFeeTypeDisplayName } from "../../feeGroups/utils/feeTypeDisplayNames";

type FeeTemplate = {
  feetemplate_id: string;
  asset: string;
  asset_type: string;
  type: string;
  fixed: string;
  percentage: string;
  min_amount: string;
  max_amount: string;
  is_active: boolean;
  decimal: number;
  network_display?: string;
};

type FeeGroupDetail = {
  feegroup_id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  created_date: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  feegroupId: string;
}

const formatDecimal = (value: string, decimals: number): string => {
  if (!value || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(decimals);
};

const FeeGroupDetailModal: React.FC<Props> = ({ isOpen, onClose, feegroupId }) => {
  const [feeGroupDetail, setFeeGroupDetail] = useState<FeeGroupDetail | null>(null);
  const [feeTemplates, setFeeTemplates] = useState<FeeTemplate[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (isOpen && feegroupId) {
      fetchFeeGroupDetail();
    }
  }, [isOpen, feegroupId]);

  const fetchFeeGroupDetail = async () => {
    if (!feegroupId) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/feegroup/feegroup-detail?feegroup_id=${feegroupId}&type=subaccount`
      );

      const data = response.data?.feegroup?.payload;
      if (data) {
        setFeeGroupDetail(data.feegroup);

        // Format templates with proper decimal places
        const formattedTemplates = (data.feetemplates || []).map((t: FeeTemplate) => ({
          ...t,
          fixed: formatDecimal(t.fixed, t.decimal),
          percentage: formatDecimal(t.percentage, 2),
          min_amount: formatDecimal(t.min_amount, t.decimal),
          max_amount: formatDecimal(t.max_amount, t.decimal),
        }));
        setFeeTemplates(formattedTemplates);

        // Initialize openSections with first type open
        const types: string[] = [...new Set((data.feetemplates || []).map((t: FeeTemplate) => t.type))].sort() as string[];
        const initialOpenState: { [key: string]: boolean } = {};
        types.forEach((type, index) => {
          initialOpenState[type] = index === 0;
        });
        setOpenSections(initialOpenState);
      }
    } catch (error: any) {
      console.error("Error fetching fee group detail:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const groupedTemplates = useMemo(() => {
    const groups: { [key: string]: FeeTemplate[] } = {};
    feeTemplates.forEach((template) => {
      if (!groups[template.type]) {
        groups[template.type] = [];
      }
      groups[template.type].push(template);
    });
    return groups;
  }, [feeTemplates]);

  const areAllOpen = Object.keys(openSections).length > 0 && Object.values(openSections).every(Boolean);

  const toggleAllSections = () => {
    const newState = !areAllOpen;
    setOpenSections(
      Object.keys(openSections).reduce((acc, key) => ({ ...acc, [key]: newState }), {})
    );
  };

  const handleClose = () => {
    setFeeGroupDetail(null);
    setFeeTemplates([]);
    setOpenSections({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={true} size="4xl">
      <ModalOverlay className="bg-[#000] !opacity-30" />
      <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-4xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[5vh]">
        <ModalBody className="p-6 md:p-7">
          {/* Header */}
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Fee Group Detail
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {feeGroupDetail ? (feeGroupDetail.description || feeGroupDetail.name) : "View fee group information"}
              </p>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <ImSpinner8 className="spinner text-primary-500" style={{ fontSize: '1.8rem' }} />
            </div>
          ) : feeGroupDetail ? (
            <div className="max-h-[60vh] overflow-y-auto">
              {Object.keys(groupedTemplates).length > 0 ? (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-navy-700">Fee Templates</h3>
                    <button
                      type="button"
                      onClick={toggleAllSections}
                      className="text-sm font-medium text-navy-700"
                    >
                      {areAllOpen ? "Collapse All" : "Show All"}
                    </button>
                  </div>
                  {Object.keys(groupedTemplates)
                    .sort()
                    .map((type) => (
                      <FeeTemplateSectionReadOnly
                        key={type}
                        title={getFeeTypeDisplayName(type)}
                        templates={groupedTemplates[type]}
                        isOpen={openSections[type] ?? false}
                        onToggle={() => setOpenSections((prev) => ({ ...prev, [type]: !prev[type] }))}
                      />
                    ))}
                </>
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  No record found
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-500">
              No fee group details available
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-2">
            <Button
              onClick={handleClose}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              Close
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FeeGroupDetailModal;
